/**
 * Renders a chat message attachment from a stored `file_url` value.
 *
 * Handles three cases:
 *   1. Legacy full-URL rows → used directly.
 *   2. `path:` prefixed rows → signed on demand against the chat-attachments
 *      bucket and refreshed when the signed URL expires.
 *   3. Empty / failed-to-sign rows → renders a non-clickable "unavailable"
 *      placeholder (instead of the previous blank bubble).
 *
 * Caller can pass a custom Supabase client (used by the storefront chat
 * widget which talks via the anon visitor session).
 */
import { useEffect, useState } from "react";
import { File as FileIcon, AlertCircle, Loader2 } from "lucide-react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { resolveChatAttachmentUrl } from "@/lib/chatAttachments";

interface Props {
  fileUrl: string | null | undefined;
  fileName: string | null;
  fileType: string | null;
  client?: SupabaseClient;
  variant?: "outgoing" | "incoming";
}

export default function ChatAttachment({ fileUrl, fileName, fileType, client, variant = "incoming" }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    resolveChatAttachmentUrl(fileUrl, client).then(u => { if (alive) { setUrl(u); setLoading(false); } });
    return () => { alive = false; };
  }, [fileUrl, client]);

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] opacity-70">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading attachment…
      </span>
    );
  }
  if (!url) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] opacity-70" title="Attachment unavailable">
        <AlertCircle className="w-3 h-3" /> {fileName || "Attachment unavailable"}
      </span>
    );
  }
  const linkClass = variant === "outgoing"
    ? "flex items-center gap-1 text-primary-foreground/90 hover:text-primary-foreground"
    : "flex items-center gap-1 text-primary hover:underline";
  if (fileType?.startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={linkClass}>
        <img src={url} alt={fileName || "image"} className="max-w-full rounded-lg max-h-40 mt-1" loading="lazy" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={linkClass}>
      <FileIcon className="w-3 h-3" />
      <span className="underline truncate">{fileName || "Attachment"}</span>
    </a>
  );
}
